import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import useBetaVersion from '@hooks/useBetaVersion/useBetaVersion';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces';
import SvgExtracosts from '@components/ringil3/icons/heading/Extracosts';
import SvgAuditlog from '@components/ringil3/icons/heading/Auditlog';
import SvgLkw from '@components/ringil3/icons/heading/Lkw';
import { extraCostsTotal } from '@components/ringil3/Features/Expedition/ExtraCosts/extraCostsTotal.ts';
import { ApiContext } from '@api/api';
import Heading from '@components/ringil3/Heading/Heading';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import {
  AuditEntityTypeEnum,
  CodeTypeEnum,
  EntityTypeEnum,
  ExpeditionDetailV4OutDTO,
  ExpeditionStatusEnum,
  DefaultVersionEnum,
  FeaturesEnum,
  InvoiceRebillingTypeEnum,
  LoadingTypesEnum,
  LocationPreviewV4OutDTO,
  LovDTO,
  NotifyTargetTypeEnum,
  PermissionTypesEnum,
} from '@api/ringil-api.v2';
import {
  useCreateUpdateExpeditionNameSidebar,
} from '@components/ringil3/Features/Expedition/Sidebars/UpdateExpeditionNameSidebar';
import { Panel, Ringil3BadgeRow } from '@components/ringil3/styles';
import DistanceBadge from '@components/ringil3/Badges/DistanceBadge';
import WeightBadge from '@components/ringil3/Badges/WeightBadge';
import VolumeBadge from '@components/ringil3/Badges/VolumeBadge';
import AiDispatcherChangeBadge from '@components/ringil3/Badges/AiDispatcherChangeBadge';
import PiecesBadge from '@components/ringil3/Badges/PiecesBadge';
import LocationFromBadge from '@components/ringil3/Badges/LocationFromBadge';
import LocationToBadge from '@components/ringil3/Badges/LocationToBadge';
import StateTag from '@components/framework/tags/StateTag.tsx';
import ManipulationTag from '@components/framework/tags/ManipulationTag.tsx';
import TruckTag from '@components/framework/tags/TruckTag.tsx';
import CargoTypeTag from '@components/framework/tags/CargoTypeTag.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import RequirementsTag from '@components/framework/tags/RequirementsTag.tsx';
import ExpeditionTypeTag from '@components/framework/tags/ExpeditionTypeTag.tsx';
import TransportationTypeTag from '@components/framework/tags/TransportationTypeTag.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import NavigationBreadCrumb from '@components/ringil3/BreadCrumb/NavigationBreadCrumb';
import ExpeditionLocation from '@components/ringil3/ExpeditionLocation/ExpeditionLocation';
import ExtraCosts from '@components/ringil3/Features/Expedition/ExtraCosts/ExtraCosts';
import LinkedSNBadge from '@components/ringil3/Badges/LinkedSNBadge';
import LinkedEntitiesBadge from '@components/ringil3/Badges/LinkedEntitiesBadge';
import CarrierNoteTag from '@components/framework/tags/CarrierNoteTag.tsx';
import EntityAuditLog, { EntityAuditLogHandle } from '@components/RingilComponents/audit/EntityAuditLog.tsx';
import TabExpeditionStateNew from '@components/ringil3/Features/Expedition/TabExpeditionStateNew';
import TabExpeditionQuotes from '@components/ringil3/Features/Expedition/TabExpeditionQuotes';
import TabExpeditionAssignedPlus from '@components/ringil3/Features/Expedition/TabExpeditionAssignedPlus';
import SvgRoute from '@components/ringil3/icons/heading/Route';
import { useCreateAttachmentComponent } from '@components/ringil3/Features/Attachments/AttachementComponent';
import SubscribeComponent from '@components/obsolete/Subscribe/SubscribeComponent.tsx';
import CustomerChatComponent from '@components/obsolete/Chat/CustomerChatComponent.tsx';
import { useCreateRingilExpeditionTabs } from '@components/ringil3/RingilTabs/RingilTabs.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import CustomsDetail from '@app/pages/Customs/CustomsDetail.tsx';
import { CargoType } from '@components/framework/input/InputCargoType/InputCargoType.tsx';
import ResponsiblePersonTag from '@components/framework/tags/ResponsiblePersonTag.tsx';
import InternalInfoInvoiceState from '@components/framework/tags/InternalInfoInvoiceStateTag.tsx';
import InternalInfoDisponentTag from '@components/framework/tags/InternalInfoDisponentTag.tsx';
import InternalInfoPairingSymbol from '@components/framework/tags/InternalInfoPairingSymbol.tsx';
import InternalNoteTag from '@components/framework/tags/InternalNoteTag.tsx';
import InternalInfoRebillingTag, { RebillingType } from '@components/framework/tags/InternalInfoRebillingTag.tsx';
import ExpeditionMoreButtons from '@components/ringil3/Features/Expedition/MoreButton/ExpeditionMoreButtons.tsx';
import InternalInfoFoldersTag from '@components/framework/tags/InternalInfoFoldersTag.tsx';
import CMRTableTab from '@components/CMR/CMRTableTab.tsx';
import TabExpeditionArrangedQuotes from '@components/ringil3/Features/Expedition/TabExpeditionArrangedQuotes.tsx';
import LinkedLadenFactorParentExpeditionBadge
  from '@components/ringil3/Badges/LinkedLadenFactorParentExpeditionBadge.tsx';
import HereMaps from '@components/features/HereMap/HereMaps.tsx';
import IncotermsTag from '@components/framework/tags/IncotermsTag.tsx';
import InternalInfoCustomDeclarantTag from '@components/framework/tags/InternalInfoCustomDeclarantTag.tsx';
import TagHider from '@components/RingilComponents/tags/TagHider.tsx';
import InternalInfoCatalogTag from '@components/framework/tags/InternalInfoCatalogTag.tsx';
import PriceBadge from '@components/ringil3/Badges/PriceBadge.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import clsx from 'clsx';
import { stringFromTimeResponse } from '@utils/dateWithTz.tsx';
import InputTextTag from '@components/RingilComponents/tags/InputTextTag.tsx';
import PolandSentTag from '@components/RingilComponents/tags/PolandSentTag.tsx';
import NotifyUsersTag from '@components/RingilComponents/tags/NotifyUsersTag.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { useRequestCarrierSidebar } from '@components/ringil3/Features/Expedition/Sidebars/RequestCarrierSidebar.tsx';
import RobotSuggestions from '@components/features/ai-dispatcher/suggestions/RobotSuggestions.tsx';
import { getAiCarrierAutoSelectionDto, isLadenFactorEligible } from '@utils/expeditionUtils.ts';
import InternalCommentsSidebar, { InternalCommentsHeaderButton } from '@components/ringil3/Features/Expedition/Sidebars/InternalCommentsSidebar.tsx';
import { EXPEDITION_RULE_DESCRIPTION } from '@components/ringil3/Features/Expedition/expeditionSecurityTexts';

interface ComponentProps {
  expeditionId: string;
  openMessages?: boolean;
  selectedCarrierId?: string;
  /**
   * Shown inside another page (the expedition list's split view, RD-2957): no breadcrumbs, and the
   * heading drops to h2 — the page already has its own title. Everything else is the full detail.
   */
  embedded?: boolean;
  /** Called after the detail reloaded itself following a change — e.g. to refresh the list beside it. */
  onChanged?: () => void;
}

const ExpeditionDetail: React.FC<ComponentProps> = ({ expeditionId, openMessages = false, selectedCarrierId: selectedCarrierIdProp, embedded = false, onChanged }) => {
  const {
    getExpeditionV4Detail,
    manualWebhookExpeditionAvailability,
    editHeaderExpedition,
    editInternalInfoExpedition,
    updateExpeditionIncoterms,
    updateExpeditionHuEkaer,
    updateExpeditionPolandSent,
    updateExpeditionRoUit,
    updateExpeditionUtnCode,
    updateExpeditionTrackingNumber,
    setNotifyTargets,
  } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const [expedition, setExpedition] = useState<ExpeditionDetailV4OutDTO>();
  // RD-2977: the backend answers 403 when the user may not see any of the expedition's folders
  const [folderAccessDenied, setFolderAccessDenied] = useState(false);
  const [webhookAvailable, setWebhookAvailable] = useState(false);
  const auditLogRef = useRef<EntityAuditLogHandle>(null);
  const nav = useNavigation();
  const { loggedUser, hasFeature } = useContext(AuthenticatedUserContext);
  const acRef = React.useRef<AbortController | null>();

  const { RequestCarrierSidebar, setVisible: setVisibleRequestCarrierSidebar } = useRequestCarrierSidebar();

  /**
   * Carriers a robot suggestion handed to the distribution sidebar, plus what to do once the
   * distribution actually goes out — the suggestion is only marked accepted then, never on opening
   * the form, because the user may still close it.
   */
  const [suggestedCarrierIds, setSuggestedCarrierIds] = useState<string[]>();
  const onSuggestionDistributed = useRef<() => void>();

  const hideTagsFlag = useSecuredFlag('feature-hide-empty-tag', {
    kind: 'feature',
    description: 'Empty tags are hidden by default',
    features: [FeaturesEnum.HIDE_EMPTY_TAG],
  });
  const hideTagsEnabled = hideTagsFlag.granted;

  const isImportedState = expedition?.expeditionStatus === ExpeditionStatusEnum.IMPORTED;

  const editHeader = useSecuredFlag('edit-header', {
    kind: 'editability',
    description: 'Edit shipment header (badges, tags, name)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_SHIPMENT_RW],
    state: [{ label: 'expeditionStatus ≠ IMPORTED', satisfied: !isImportedState, description: EXPEDITION_RULE_DESCRIPTION.imported }],
  });
  const editHeaderPermission = editHeader.granted;
  const showHeader = useSecuredFlag('show-header', {
    kind: 'visibility',
    description: 'See shipment header section',
    permissions: [
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_SHIPMENT_READ,
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_SHIPMENT_RW,
    ],
  });
  const showHeaderPermission = showHeader.granted;
  const showShipment = useSecuredFlag('show-shipment', {
    kind: 'visibility',
    description: 'See shipment tab',
    permissions: [
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_SHIPMENT_READ,
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_SHIPMENT_RW,
    ],
  });
  const showShipmentPermission = showShipment.granted;
  const moreAction = useSecuredFlag('more-action', {
    kind: 'action',
    description: 'Show more-actions menu (subscribe, archive, duplicate, …)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_SHIPMENT_RW],
  });
  const editShipment = useSecuredFlag('edit-shipment', {
    kind: 'editability',
    description: 'Edit shipment name, route, locations',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_SHIPMENT_RW],
    state: [{ label: 'expeditionStatus ≠ IMPORTED', satisfied: !isImportedState, description: EXPEDITION_RULE_DESCRIPTION.imported }],
  });
  const editShipmentPermission = editShipment.granted;
  const customsPermission = useSecuredFlag('customs-permission', {
    kind: 'visibility',
    description: 'Has customs READ or RW permission',
    permissions: [
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_CUSTOMS_READ,
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_CUSTOMS_RW,
    ],
  });
  const showInternalInfo = useSecuredFlag('show-internal-info', {
    kind: 'visibility',
    description: 'See internal info tab',
    permissions: [
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_INTERNALINFO_READ,
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_INTERNALINFO_RW,
    ],
  });
  const showInternalInfoPermission = showInternalInfo.granted;
  // Audit/history is gated on its own dedicated read permission (RD-2750) — the BE endpoint 403s
  // without it. Nested under the internal-info tab (SN model): the tab opens on INTERNALINFO, and
  // this permission then reveals the audit section inside it.
  const showAudit = useSecuredFlag('show-audit-log', {
    kind: 'visibility',
    description: 'See expedition audit log / history',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_AUDITLOG_READ],
  });
  const editInternalInfo = useSecuredFlag('edit-internal-info', {
    kind: 'editability',
    description: 'Edit internal info fields',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_INTERNALINFO_RW],
    state: [{ label: 'expeditionStatus ≠ IMPORTED', satisfied: !isImportedState, description: EXPEDITION_RULE_DESCRIPTION.imported }],
  });
  const editInternalInfoResponsiblePerson = useSecuredFlag('edit-internal-info-person', {
    kind: 'editability',
    description: 'Edit internal-info Responsible Person (no IMPORTED gate)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_INTERNALINFO_RW],
  });
  const showCmr = useSecuredFlag('show-cmr', {
    kind: 'visibility',
    description: 'See CMR tab',
    permissions: [
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_CMR_READ,
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_CMR_RW,
    ],
  });
  const showCmrPermission = showCmr.granted;
  const editCmr = useSecuredFlag('edit-cmr', {
    kind: 'editability',
    description: 'Edit CMR records',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_CMR_RW],
    state: [{ label: 'expeditionStatus ≠ IMPORTED', satisfied: !isImportedState, description: EXPEDITION_RULE_DESCRIPTION.imported }],
  });
  const editCmrPermission = editCmr.granted;
  const showChat = useSecuredFlag('show-chat', {
    kind: 'visibility',
    description: 'See messages tab + chat dialog',
    permissions: [
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_CHAT_READ,
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_CHAT_RW,
    ],
  });
  const showChatPermission = showChat.granted;
  const editChat = useSecuredFlag('edit-chat', {
    kind: 'editability',
    description: 'Send messages in chat',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_CHAT_RW],
    state: [{ label: 'expeditionStatus ≠ IMPORTED', satisfied: !isImportedState, description: EXPEDITION_RULE_DESCRIPTION.imported }],
  });
  const editChatPermission = editChat.granted;
  const showInternalComment = useSecuredFlag('show-internal-comment', {
    kind: 'visibility',
    description: 'See internal comments (feature flag + READ/RW permission)',
    features: [FeaturesEnum.EXPEDITION_INTERNAL_COMMENT],
    permissions: [
      PermissionTypesEnum.PERMISSION_EXPEDITION_INTERNAL_COMMENT_READ,
      PermissionTypesEnum.PERMISSION_EXPEDITION_INTERNAL_COMMENT_RW,
    ],
  });
  const editInternalComment = useSecuredFlag('edit-internal-comment', {
    kind: 'editability',
    description: 'Add / edit / delete internal comments (feature flag + RW permission)',
    features: [FeaturesEnum.EXPEDITION_INTERNAL_COMMENT],
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_INTERNAL_COMMENT_RW],
  });
  const showAttachments = useSecuredFlag('show-attachments', {
    kind: 'visibility',
    description: 'See attachments tab',
    permissions: [
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_ATTACHMENTS_READ,
      PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_ATTACHMENTS_RW,
    ],
  });
  const showAttachmentsPermission = showAttachments.granted;
  const editAttachments = useSecuredFlag('edit-attachments', {
    kind: 'editability',
    description: 'Upload / delete attachments',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_ATTACHMENTS_RW],
    state: [{ label: 'expeditionStatus ≠ IMPORTED', satisfied: !isImportedState, description: EXPEDITION_RULE_DESCRIPTION.imported }],
  });
  const editAttachmentsPermission = editAttachments.granted;
  const showShipmentPrice = useSecuredFlag('show-price', {
    kind: 'visibility',
    description: 'See shipment price',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_PRICE],
  });
  const showDistributionFlag = useSecuredFlag('show-distribution', {
    kind: 'visibility',
    description: 'See distribution / quotes panel (ASSIGNING state)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW],
  });

  const createNewDistributionFlag = useSecuredFlag('arranged-create-distribution-no-laden-expeditions', {
    kind: 'action',
    description: 'Create new distribution button',
    features: [FeaturesEnum.MULTIPLE_DISTRIBUTIONS],
    // "No active distribution" is a state of the shipment, not "nothing to show": as `data` it would never reach the AI assistant.
    state: [
      {
        label: 'expeditionStatus ∉ {FINISHED, CANCELED}',
        satisfied: expedition?.expeditionStatus !== ExpeditionStatusEnum.FINISHED && expedition?.expeditionStatus !== ExpeditionStatusEnum.CANCELED,
        description: 'Not offered on a finished or cancelled shipment',
        byDesign: true,
      },
      {
        label: 'no active distribution',
        satisfied: !expedition?.distributions.find(distribution => distribution.isActive),
        description: 'Offered only while no distribution (round of asking carriers) is running on the shipment',
        byDesign: true,
      },
    ],
    data: [
      { label: 'No laden factor expeditions expedition?.ladenFactorExpeditions.length === 0', satisfied: expedition?.ladenFactorExpeditions.length === 0 },
    ],
  });

  const [showMap, setShowMap] = useState(false);

  const calculateTotalWeight = useMemo(() => {
    if (!expedition || !expedition.locations) return 0;

    return expedition.locations.reduce((locationSum, location) => {
      if (!location.loadedOrders) return locationSum;

      const locationWeight = location.loadedOrders.reduce((orderSum, order) => {
        return orderSum + order.orderTotalWeight;
      }, 0);

      return locationSum + locationWeight;
    }, 0);
  }, [expedition]);

  /**
   * The SNs this expedition is made of. The list already covers SNs that sat behind expeditions
   * merged away, so nothing has to be resolved through mergedExpeditions. The expedition's own
   * origin SN is rendered by LinkedSNBadge, so drop it here to avoid showing the same id twice.
   */
  const mergedSupplierNotifications = useMemo(() => {
    const ownSupplierNotificationId = expedition?.supplierNotificationLink?.supplierNotificationId;
    return (expedition?.mergedSupplierNotifications ?? []).filter(sn => sn.supplierNotificationId !== ownSupplierNotificationId);
  }, [expedition]);

  const { selectedPanel, renderTabs } = useCreateRingilExpeditionTabs({
    showShipment: showShipmentPermission,
    showCustoms: customsPermission.granted,
    showInternalInfo: showInternalInfoPermission,
    showCmr: showCmrPermission,
    showMessages: showChatPermission,
    showAttachments: showAttachmentsPermission,
  });
  const { internals: attachmentInternals, load: loadAttachments, AttachmentComponent } = useCreateAttachmentComponent(
    {
      enableCopyDocument: true,
      disabled: !editAttachmentsPermission,
      enableDownloadZip: true,
      downloadZipApplicationId: expedition?.applicationId,
      // Attachment changes don't go through load(), so the page beside an embedded detail is told here.
      onChanged,
    },
    {
      entityType: EntityTypeEnum.EXPEDITION,
      entityId: expeditionId,
      disabled: !editAttachmentsPermission,
    },
  );

  const { UpdateExpeditionNameSidebar, setVisible: setUpdateNameVisible } = useCreateUpdateExpeditionNameSidebar();

  const [selectedCarrierId, setSelectedCarrierId] = useState(selectedCarrierIdProp);
  const [isMessagesOpen, setIsMessagesOpen] = useState(openMessages);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);

  // Trigger that opens the internal-comments sidebar; placed in shipment-tab section headers.
  // Each instance needs a unique secured-area id, hence the areaId discriminator.
  const internalCommentsHeaderButton = (areaId: string) => (
    <InternalCommentsHeaderButton
      onClick={() => setIsCommentsOpen(true)}
      secured={{ id: `internal-comment-open-${areaId}`, label: 'Open internal comments', description: 'Button that opens the internal comments on this part of the shipment', visible: showInternalComment }}
    />
  );

  // Each History-tab section collapses independently per its own beta surface (NEW = collapsed).
  const extraCostsCollapsed = useBetaVersion(BetaSurface.EXPEDITION_EXTRA_COSTS).version === DefaultVersionEnum.NEW;
  const tenderHistoryCollapsed = useBetaVersion(BetaSurface.EXPEDITION_TENDER_HISTORY).version === DefaultVersionEnum.NEW;
  const auditLogCollapsed = useBetaVersion(BetaSurface.EXPEDITION_AUDIT_LOG).version === DefaultVersionEnum.NEW;

  // Called as {extraCostsSection(...)} rather than rendered as a component, so the subtree is not
  // remounted on every parent render.
  const extraCostsSection = (hideHeader: boolean) => (
    <ExtraCosts
      costs={expedition.extraCosts}
      companyId={expedition.customerId}
      applicationId={expedition.applicationId}
      onRemoved={() => load()}
      contractualPrice={expedition.sumPrice?.finalPrice}
      agreedPrice={expedition.sumPrice?.agreedPrice}
      agreedPriceCurrency={expedition.sumPrice?.agreedPriceCurrency}
      extraCostSumPrice={expedition.sumPrice?.extraCostSumPrice}
      extraCostSumCurrency={expedition.sumPrice?.extraCostSumCurrency}
      extraCostSumPriceInternal={expedition.sumPrice?.extraCostSumPriceInternal}
      hideHeader={hideHeader}
    />
  );

  const showDistributionHistory =
    isNotNullOrUndefined(expedition) &&
    [ExpeditionStatusEnum.NEW, ExpeditionStatusEnum.ASSIGNING].indexOf(expedition.expeditionStatus) === -1 &&
    expedition.distributions?.length > 0;

  const distributionHistorySection = () => (
    <>
      {expedition.distributions.map(nonActiveDistribution => (
        <TabExpeditionQuotes
          key={nonActiveDistribution.distributionId}
          expedition={expedition}
          distribution={nonActiveDistribution}
          onReload={() => load()}
          editTransportationFlag={editShipment}
          activeDistribution={false}
          showPrices={showShipmentPrice}
          showMessages={showChat}
          editMessages={editChat}
          hideHeader
        />
      ))}
    </>
  );

  const attachmentsLoadedRef = useRef(false);

  useEffect(() => {
    acRef.current = new AbortController();
    attachmentsLoadedRef.current = false;
    if (isNotNullOrUndefined(expeditionId)) {
      load();
    }
    return () => {
      acRef.current.abort();
    };
  }, [expeditionId]);

  // Attachments always load when their tab is opened, not on page mount.
  useEffect(() => {
    if (selectedPanel !== 'attachments' || attachmentsLoadedRef.current) return;
    if (isNotNullOrUndefined(expeditionId)) {
      attachmentsLoadedRef.current = true;
      loadAttachments(EntityTypeEnum.EXPEDITION, expeditionId);
    }
  }, [selectedPanel, expeditionId]);

  // The first load of an expedition is not a change; every later one follows an edit (all edit paths
  // end in load()), which is when `onChanged` tells the page around an embedded detail.
  const loadedOnceRef = useRef<string | null>(null);
  const load = () => {
    getExpeditionV4Detail(
      expeditionId,
      d => {
        if (!acRef.current.signal.aborted) {
          setExpedition(d);
          if (loadedOnceRef.current === expeditionId) onChanged?.();
          loadedOnceRef.current = expeditionId;
          auditLogRef.current?.reload();
          // Drives the "Send webhook" item of the more-actions menu; owner + definition check is server side.
          manualWebhookExpeditionAvailability(expeditionId, a => setWebhookAvailable(a.available), null, {
            successToast: false,
            onError: () => setWebhookAvailable(false),
          });
        }
        if (d.assignedCarrierId && !selectedCarrierIdProp) {
          setSelectedCarrierId(d.assignedCarrierId);
        }
      },
      null,
      {
        onError: (error, showStandardError) => {
          // RD-2977: a folder-restricted expedition - show a dedicated empty state instead of the generic toast
          if ((error as any)?.response?.status === 403) {
            setFolderAccessDenied(true);
            return;
          }
          showStandardError();
        },
      },
    );
  };

  const mapRebilling = (v: RebillingType) => {
    if (v == null) return null;

    if (v.invoiceRebillingType == InvoiceRebillingTypeEnum.EXTERNAL_REBILLING) {
      return { invoiceRebillingType: v.invoiceRebillingType, invoicePartnerId: v.invoicePartner?.companyId };
    }
    if (v.invoiceRebillingType == InvoiceRebillingTypeEnum.INTERNAL_REBILLING) {
      return { invoiceRebillingType: v.invoiceRebillingType, invoiceDepartment: v.invoiceDepartment };
    }
    return { invoiceRebillingType: v.invoiceRebillingType };
  };

  if (folderAccessDenied) {
    return (
      <div className={'flex flex-col items-center gap-4 py-16'} data-testid={'expedition-folder-access-denied'}>
        <COHeadline variant={'h2'} title={tr('ExpeditionDetail.folderNoAccessTitle', 'K této přepravě nemáte přístup')} />
        <div className={'text-content-secondary text-center max-w-[520px]'}>
          {tr('ExpeditionDetail.folderNoAccessText', 'Přeprava je zařazena do složky, ke které nemáte oprávnění. Požádejte správce společnosti o přístup ke složce.')}
        </div>
        <RingilButton mode={'filled-rectangle'} label={tr('ExpeditionDetail.backToList', 'Zpět na seznam přeprav')} onClick={() => nav.navigate(nav.urlFunctions.createCustomerExpeditionList())} />
      </div>
    );
  }

  if (isNullOrUndefined(expedition)) return <></>;

  const isAssignedPlus = [
    ExpeditionStatusEnum.ASSIGNED,
    ExpeditionStatusEnum.ACTIVE,
    ExpeditionStatusEnum.WAITING_FOR_DOCS,
    ExpeditionStatusEnum.READY_TO_SHIPMENT,
    ExpeditionStatusEnum.FINISHED,
    ExpeditionStatusEnum.CANCELED,
  ].includes(expedition.expeditionStatus);

  const tagsConfig = [
    {
      key: 'manipulation',
      filled: expedition.loadingTypes && expedition.loadingTypes.length > 0,
      element: (
        <ManipulationTag
          value={expedition.loadingTypes}
          onChange={v => {
            editHeaderExpedition(expeditionId, { loadingTypes: { loadingTypes: v as LoadingTypesEnum[] } }, () => load());
          }}
          dataTestId="expeditionInfo-manipulation"
          secured={{ id: 'tag-manipulation', label: 'ManipulationTag', description: 'Shipment header tag: handling (manipulation) type', editable: editHeader }}
        />
      ),
    },
    {
      key: 'transportation',
      filled: !!expedition.transportationType,
      element: (
        <TransportationTypeTag
          transportationType={expedition.transportationType}
          onChange={v => editHeaderExpedition(expeditionId, { transportationType: { transportationType: v } }, () => load())}
          secured={{ id: 'tag-transportation', label: 'TransportationTypeTag', description: 'Shipment header tag: transportation type', editable: editHeader }}
        />
      ),
    },
    {
      key: 'expedition-type',
      filled: !!expedition.expeditionType,
      element: (
        <ExpeditionTypeTag
          value={expedition.expeditionType}
          onChange={v => editHeaderExpedition(expeditionId, { expeditionType: { expeditionType: v } }, () => load())}
          secured={{ id: 'tag-expedition-type', label: 'ExpeditionTypeTag', description: 'Shipment header tag: shipment type', editable: editHeader }}
        />
      ),
    },
    {
      key: 'truck-type',
      filled: !!expedition.truckType?.code,
      element: (
        <TruckTag
          truckTypeCode={expedition.truckType?.code}
          onChange={v => editHeaderExpedition(expeditionId, { truckType: { truckTypeCode: v } }, () => load())}
          secured={{ id: 'tag-truck-type', label: 'TruckTag', description: 'Shipment header tag: truck type', editable: editHeader }}
        />
      ),
    },
    {
      key: 'cargo-type',
      filled: !!expedition.cargoTypeCode,
      element: (
        <CargoTypeTag
          cargoType={{ cargoTypeCode: expedition.cargoTypeCode, adrWeight: expedition.adrWeight } as CargoType}
          onChange={v => {
            editHeaderExpedition(
              expeditionId,
              {
                cargoType: {
                  cargoTypeCode: v.cargoTypeCode,
                  adrWeight: v.adrWeight,
                },
              },
              () => load(),
            );
          }}
          secured={{ id: 'tag-cargo-type', label: 'CargoTypeTag', description: 'Shipment header tag: cargo type', editable: editHeader }}
        />
      ),
    },
    {
      key: 'incoterms',
      filled: !!expedition.incoterms,
      element: (
        <IncotermsTag
          value={expedition.incoterms}
          onChange={v => {
            updateExpeditionIncoterms(expeditionId, v, () => load());
          }}
          dataTestId="expeditionInfo-incoterms"
          dataTestIdList="expeditionInfo-incotermsList"
          dataTestIdInput="expeditionInfo-incotermsInput"
          secured={{ id: 'tag-incoterms', label: 'IncotermsTag', description: 'Shipment header tag: Incoterms delivery terms', editable: editHeader }}
        />
      ),
    },
    {
      key: 'requirements',
      filled: !!expedition.requirementCodes,
      element: (
        <RequirementsTag
          valueCodes={expedition.requirementCodes}
          companyId={expedition.customerId}
          onChange={v => {
            editHeaderExpedition(expeditionId, { specialRequirements: { specialRequirementCodes: v } }, () => load());
          }}
          dataTestId="expeditionInfo-carrierRequirements"
          secured={{ id: 'tag-requirements', label: 'RequirementsTag', description: 'Shipment header tag: special requirements for the transport', editable: editHeader }}
        />
      ),
    },
    {
      key: 'huEkaerNumber',
      filled: !!expedition.huEkaerNumber,
      element: (
        <InputTextTag
          label={tr(`InputTextTag.ekaerNumber`, `Ekaer number`)}
          value={expedition.huEkaerNumber}
          onChange={(value: string) => {
            updateExpeditionHuEkaer(expeditionId, { huEkaerNumber: value }, () => load());
          }}
          dataTestId="expeditionInfo-ekaerNumber"
          dataTestIdInput="expeditionInfo-ekaerNumberInput"
          secured={{ id: 'tag-hu-ekaer', label: 'HU Ekaer number', description: 'Shipment header tag: Hungarian EKAER number', editable: editHeader }}
        />
      ),
    },
    {
      key: 'roUitNumber',
      filled: !!expedition.roUitNumber,
      element: (
        <InputTextTag
          label={tr(`InputTextTag.uitNumber`, `Uit number`)}
          value={expedition.roUitNumber}
          onChange={(value: string) => {
            updateExpeditionRoUit(expeditionId, { roUitNumber: value }, () => load());
          }}
          dataTestId="expeditionInfo-uitNumber"
          dataTestIdInput="expeditionInfo-uitNumberInput"
          secured={{ id: 'tag-ro-uit', label: 'RO UIT number', description: 'Shipment header tag: Romanian UIT number', editable: editHeader }}
        />
      ),
    },
    {
      key: 'utnCode',
      filled: !!expedition.utnCode,
      element: (
        <InputTextTag
          label={tr(`InputTextTag.utnCode`, `Utn code`)}
          value={expedition.utnCode}
          onChange={(value: string) => {
            updateExpeditionUtnCode(expeditionId, { utnCode: value }, () => load());
          }}
          secured={{ id: 'tag-utn-code', label: 'UTN code', description: 'Shipment header tag: UTN code', editable: editHeader }}
        />
      ),
    },
    {
      key: 'trackingNumber',
      filled: !!expedition.trackingNumber,
      element: (
        <InputTextTag
          label={tr(`InputTextTag.trackingNumber`, `Tracking number`)}
          value={expedition.trackingNumber}
          onChange={(value: string) => {
            updateExpeditionTrackingNumber(expeditionId, { trackingNumber: value }, () => load());
          }}
          secured={{ id: 'tag-tracking-number', label: 'Tracking number', editable: editHeader }}
        />
      ),
    },
    {
      key: 'polandSent',
      filled: !!expedition.polandSent,
      element: (
        <PolandSentTag
          value={expedition.polandSent}
          onChange={(value) => {
            updateExpeditionPolandSent(expeditionId, value, () => load());
          }}
          dataTestId="expeditionInfo-polandSentSystem"
          dataTestIdDeclarationRefNumberInput="expeditionInfo-polandSentSystem-declarationRefNumberInput"
          dataTestIdCarrierUpdateKeyInput="expeditionInfo-polandSentSystem-carrierUpdateKeyInput"
          secured={{ id: 'tag-poland-sent', label: 'PolandSentTag', description: 'Shipment header tag: Polish SENT carrier number and update key', editable: editHeader }}
        />
      ),
    },
  ];

  const getTotalHandlingUnits = (locations: LocationPreviewV4OutDTO[]) => {
    if (!locations) return 0;
    return locations
      .flatMap(loc => loc.loadedOrders || [])
      .flatMap(order => order.items || [])
      .reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const submitToSapEnabled = expedition.expeditionStatus !== ExpeditionStatusEnum.FINISHED
    && !expedition.ladenFactorParentExpedition
    && hasFeature([FeaturesEnum.SKODA_PUBLISH_EXPEDITION_TO_SAP])
    && (
      expedition.ladenFactorExpeditions?.length > 0
      || (
        expedition.ladenFactorExpeditions.length == 0
        && expedition.expeditionStatus === ExpeditionStatusEnum.ASSIGNED
      )
    );

  const activeDistribution = expedition.distributions?.find(d => d.isActive)
    || (expedition.distributions && expedition.distributions.length > 0
      ? [...expedition.distributions].sort((a, b) => {
          // Use utcEnd when timezoneId is available, otherwise fall back to validTill
          const at = a.timezoneId && a.utcEnd
            ? new Date(a.utcEnd).getTime()
            : (a.validTill ? new Date(stringFromTimeResponse(a.validTill)).getTime() : -Infinity);
          const bt = b.timezoneId && b.utcEnd
            ? new Date(b.utcEnd).getTime()
            : (b.validTill ? new Date(stringFromTimeResponse(b.validTill)).getTime() : -Infinity);
          return bt - at;
        })[0]
      : undefined);

  if (isNullOrUndefined(showHeaderPermission)) return <></>;
  return (
    <>
      {!embedded && (
      <BreadCrumbComponent
        items={[
          {
            label: tr(`ExpeditionDetail.expeditionList`, `Expedition list`),
            url: nav.createNavigationLink(nav.urlFunctions.createCustomerExpeditionList()),
          },
          { label: expedition?.applicationId || tr(`ExpeditionDetail.expeditionDetail`, `Expedition detail`) },
        ]}
      />
      )}

      <Heading
        title={expedition.applicationId}
        // Embedded, the shipment number is the way to its full page.
        titleLink={embedded ? nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(expedition.id)) : undefined}
        subtitle={expedition.name}
        isFavorite={expedition.isFavorite}
        onEdit={editShipmentPermission ? () => setUpdateNameVisible(true) : undefined}
        headingVariant={embedded ? 'h2' : 'h1'}
        labels={
          <LabelsTag
            objectId={expedition.id}
            objectType={EntityTypeEnum.EXPEDITION}
            resolveLabelsForType={EntityTypeEnum.EXPEDITION}
            dataTestId="expeditionInfo-label"
            secured={{ id: 'header-labels', label: 'LabelsTag', description: "The shipment's labels", editable: editHeader }}
          />
        }
        buttons={[
          <SubscribeComponent
            key="subscribe"
            customerId={expedition.customerId}
            applicationId={expedition.applicationId}
            secured={{ id: 'header-subscribe', label: 'SubscribeComponent', description: 'Subscribe to notifications about changes to the shipment', visible: moreAction }}
          />,
                <ExpeditionMoreButtons
                  key="more-buttons"
                  secured={{ id: 'header-more-button', label: 'ExpeditionMoreButtons', description: 'Menu with more actions on the shipment (duplicate, archive, cancel and others)', visible: moreAction }}
                  expedition={{
                    id: expedition.id,
                    customerId: expedition.customerId,
                    applicationId: expedition.applicationId,
                    currency: expedition.expeditionCurrency,
                    expeditionStatus: expedition.expeditionStatus,
                    isArchived: expedition.isArchived,
                    isFavorite: expedition.isFavorite,
                    webhookAvailable,
                    fromLine: !!expedition.tmplV2Id,
                    isDistributionCreated: expedition.isDistributionCreated === true,
                    linkedToSN: expedition.supplierNotificationLink != null,
                    firstLocationName: expedition.locations[0].name,
                    assignedDispatcherEmail: expedition.carrierDispatcherEmail,
                    carrierApiType: expedition.distributions?.flatMap(distr => distr.selectedItems).find(i => i.carrierOrGroup === 'CARRIER')?.apiType ?? null,
                    duplicationOptions: {
                      ladenFactorEligible: isLadenFactorEligible(expedition, hasFeature([FeaturesEnum.LADEN_FACTOR])),
                      handlingUnitCount: getTotalHandlingUnits(expedition.locations),
                      originalLadenFactor: expedition.ladenFactor,
                      originalAllowPartialDelivery: expedition.allowPartialDelivery,
                    },
                    showExtraCosts:
                      expedition.expeditionStatus !== ExpeditionStatusEnum.IMPORTED &&
                      expedition.expeditionStatus !== ExpeditionStatusEnum.NEW &&
                      expedition.expeditionStatus !== ExpeditionStatusEnum.ASSIGNING &&
                      expedition.expeditionStatus !== ExpeditionStatusEnum.CANCELED &&
                      expedition.isCarrierAssigned,
                    submitToSapEnabled,
                  }}
                  onReload={() => load()}
                  refreshAttachment={() => loadAttachments(EntityTypeEnum.EXPEDITION, expeditionId)}
                  buttonSize={'big'}
                  dataTestId='expeditionDetail-moreButton'
                />,
        ]}
        /* The heading is a flex item, so this z-index creates a stacking context even though the row is
           statically positioned — everything opened from the header (labels popup, more-button menu) is
           trapped inside it. It has to stay above the sticky tabs strip (--z-tabs). */
        className={clsx('z-[var(--z-tabs-above)]', embedded && 'mb-4')}
      />

      <SecuredArea id="header-section" visible={showHeader} label="Header (badges, tags, carrier note)" description="The shipment header: summary badges, tags and the note for the carrier">
        <Ringil3BadgeRow>
          {expedition.ladenFactorParentExpedition && <LinkedLadenFactorParentExpeditionBadge ladenFactorParentExpedition={expedition.ladenFactorParentExpedition} />}
          <LinkedSNBadge supplierNotifications={expedition.supplierNotificationLink} />
          {expedition.mergedToExpedition && (
            <LinkedEntitiesBadge
              label={tr('ExpeditionDetail.mergedToExpedition', 'Merged into')}
              links={[
                {
                  id: expedition.mergedToExpedition.expeditionId,
                  applicationId: expedition.mergedToExpedition.expeditionApplicationId,
                  to: nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(expedition.mergedToExpedition.expeditionId)),
                },
              ]}
              dataTestId="expeditionSummary-mergedToExpedition"
            />
          )}
          <LinkedEntitiesBadge
            label={tr(
              'ExpeditionDetail.mergedExpeditionsCount',
              '{{count}} shipments',
              { count: expedition.mergedExpeditions?.length ?? 0 },
              expedition.mergedExpeditions?.length ?? 0,
            )}
            links={(expedition.mergedExpeditions ?? []).map(mergedExpedition => ({
              id: mergedExpedition.expeditionId,
              applicationId: mergedExpedition.expeditionApplicationId,
              to: nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(mergedExpedition.expeditionId)),
            }))}
            strikeThrough={true}
            dataTestId="expeditionSummary-mergedExpeditions"
          />
          <LinkedSNBadge
            supplierNotifications={mergedSupplierNotifications}
            label={tr(
              'ExpeditionDetail.mergedSupplierNotificationsCount',
              '{{count}} supplier notifications',
              { count: mergedSupplierNotifications.length },
              mergedSupplierNotifications.length,
            )}
            dataTestId="expeditionSummary-mergedSupplierNotifications"
          />
          <div data-testid="expeditionSummary-distance">
            <DistanceBadge distance={expedition.distance} />
          </div>
          <PriceBadge
            priceDto={expedition.sumPrice?.finalPriceDTO}
            dataTestId="expeditionSummary-totalPrice"
            secured={{
              id: 'badge-price',
              label: 'PriceBadge',
              description: "Summary badge with the shipment's total price",
              visible: [
                showShipmentPrice,
                {
                  type: 'data',
                  label: 'sumPrice.finalPriceDTO present',
                  satisfied: !!expedition.sumPrice?.finalPriceDTO,
                },
              ],
            }}
          />
          <div data-testid="expeditionSummary-handlingUnits">
            <PiecesBadge piecesCount={expedition.handlingUnitCount} />
          </div>
          <div data-testid="expeditionSummary-from">
            <LocationFromBadge locationName={expedition.locations[0].name} />
          </div>
          <div data-testid="expeditionSummary-to">
            <LocationToBadge locationName={expedition.locations[expedition.locations.length - 1].name} />
          </div>
          <div data-testid="expeditionSummary-weight">
            <WeightBadge weight={calculateTotalWeight} />
          </div>
          <VolumeBadge
            volume={expedition.totalVolume}
            dataTestId="expeditionSummary-volume"
            secured={{
              id: 'badge-volume',
              label: 'VolumeBadge',
              description: "Summary badge with the shipment's total volume",
              visible: [
                {
                  // A company setting, not "nothing to show": as `data` it would never reach the AI assistant.
                  type: 'custom',
                  label: 'volumeOfOrderEnabled',
                  satisfied: expedition.volumeOfOrderEnabled,
                  description: "Shown only when volume of order is turned on for the shipment's company",
                  byDesign: false,
                },
              ],
            }}
          />
          <AiDispatcherChangeBadge
            firstChangeAt={expedition.aiDispatcherFirstChangeAt}
            lastChangeAt={expedition.aiDispatcherLastChangeAt}
            timezoneId={loggedUser?.defaultTimezoneId || 'Europe/Prague'}
            dataTestId="expeditionSummary-aiDispatcherChange"
            secured={{
              id: 'badge-ai-dispatcher-change',
              label: 'AiDispatcherChangeBadge',
              description: 'Summary badge with the first and last AI Dispatcher change of the shipment',
              visible: [{ type: 'data', label: 'aiDispatcherLastChangeAt present', satisfied: !!expedition.aiDispatcherLastChangeAt }],
            }}
          />
        </Ringil3BadgeRow>
        <Ringil3BadgeRow>
          <StateTag
            expeditionState={expedition.expeditionStatus}
            cancellationReasonCode={expedition.cancellationReasonCode}
            dataTestId="expeditionInfo-status"
          />
          <TagHider
            tags={tagsConfig}
            showAllByDefault={!hideTagsEnabled}
          />
        </Ringil3BadgeRow>
        <Ringil3BadgeRow>
          <CarrierNoteTag
            value={expedition.carrierNote}
            fullWidth
            onChange={v => {
              editHeaderExpedition(expeditionId, { carrierNote: { carrierNote: v } }, () => load());
            }}
            dataTestId="expeditionInfo-noteForCarrier"
            dataTestIdInput="expeditionInfo-noteForCarrierInput"
            secured={{ id: 'tag-carrier-note', label: 'CarrierNoteTag', description: 'Shipment header tag: note for the carrier', editable: editHeader }}
          />
        </Ringil3BadgeRow>
      </SecuredArea>

      {/* What the robot suggests doing next. Above the tabs rather than inside the expedition one:
          advice that needs a decision should not be hidden by whichever tab happens to be open.
          Renders nothing when there is nothing open, which is the usual case. */}
      <RobotSuggestions
        expedition={expedition}
        onExpeditionChanged={() => load()}
        onDistribute={(carrierIds, onDistributed) => {
          setSuggestedCarrierIds(carrierIds);
          onSuggestionDistributed.current = onDistributed;
          setVisibleRequestCarrierSidebar(true);
        }}
      />

      {renderTabs({
        onMessagesClick: () => {
          setSelectedCarrierId(expedition.assignedCarrierId);
          setIsMessagesOpen(true);
        },
      })}

      {selectedPanel === 'expedition' && (
        <>
          <SecuredArea id="arranged-create-distribution-no-laden-expeditions" label="Create new distribution (no laden expeditions)" description="Button that starts a new distribution: a new round of asking carriers for quotes" visible={createNewDistributionFlag}>
            <div className="flex flex-row justify-end">
              <RingilButton
                mode={'outlined-rectangle'}
                onClick={() => {
                  // A distribution started by hand must not inherit a suggestion's carriers.
                  setSuggestedCarrierIds(undefined);
                  onSuggestionDistributed.current = undefined;
                  setVisibleRequestCarrierSidebar(true);
                }}
                label={tr('TabExpeditionArrangedQuotes.createNewDistribution', 'Create new distribution')}
              />
            </div>
          </SecuredArea>
          <TabExpeditionArrangedQuotes
            expedition={expedition}
            onReload={() => load()}
            secured={{
              id: 'tab-arranged-quotes',
              label: 'TabExpeditionArrangedQuotes',
              visible: [
                {
                  type: 'data',
                  label: 'ladenFactorExpeditions.length > 0',
                  satisfied: expedition.ladenFactorExpeditions?.length > 0,
                },
              ],
            }}
          />
          <TabExpeditionStateNew
            expedition={expedition}
            onReload={() => load()}
            secured={{
              id: 'tab-state-new',
              label: 'TabExpeditionStateNew',
              description: 'Carrier section of a new shipment: request quotes from carriers or assign a carrier directly',
              visible: [
                editShipment,
                {
                  type: 'state',
                  label: 'expeditionStatus === NEW',
                  satisfied: expedition.expeditionStatus === ExpeditionStatusEnum.NEW,
                  description: 'Shown only while the shipment is New, before any carrier is asked or assigned',
                  byDesign: true,
                },
              ],
            }}
          />
          <SecuredArea
            id="tab-assigning"
            label="ASSIGNING distributions block"
            description="The shipment's distributions and the carriers' quotes while a carrier is being selected"
            visible={[
              showDistributionFlag,
              {
                type: 'state',
                label: 'expeditionStatus === ASSIGNING',
                satisfied: expedition.expeditionStatus === 'ASSIGNING',
                description: 'Shown only while carriers are being asked for quotes (status Assigning)',
                byDesign: true,
              },
            ]}
          >
            {activeDistribution && (
              <TabExpeditionQuotes
                key={activeDistribution.distributionId}
                expedition={expedition}
                distribution={activeDistribution}
                onReload={() => load()}
                showPrices={showShipmentPrice}
                editTransportationFlag={editShipment}
                activeDistribution={true}
                showMessages={showChat}
                editMessages={editChat}
                headerActions={internalCommentsHeaderButton(`quotes-${activeDistribution.distributionId}`)}
              />
            )}
            {expedition.distributions.filter(distr => distr.distributionId !== activeDistribution?.distributionId).map(nonActiveDistribution => (
              <TabExpeditionQuotes
                key={nonActiveDistribution.distributionId}
                expedition={expedition}
                distribution={nonActiveDistribution}
                onReload={() => load()}
                editTransportationFlag={editShipment}
                activeDistribution={false}
                showPrices={showShipmentPrice}
                showMessages={showChat}
                editMessages={editChat}
                headerActions={internalCommentsHeaderButton(`quotes-${nonActiveDistribution.distributionId}`)}
              />
            ))}
          </SecuredArea>
          <TabExpeditionAssignedPlus
            expedition={expedition}
            onReload={() => load()}
            canEdit={editShipmentPermission}
            secured={{
              id: 'tab-assigned-plus',
              label: 'TabExpeditionAssignedPlus',
              description: "The shipment's assigned carrier, once a carrier has been chosen",
              visible: [
                {
                  type: 'state',
                  label: 'expeditionStatus ∈ {ASSIGNED, ACTIVE, WAITING_FOR_DOCS, READY_TO_SHIPMENT, FINISHED, CANCELED}',
                  satisfied: isAssignedPlus,
                  description: 'Shown only once a carrier is assigned to the shipment',
                  byDesign: true,
                },
              ],
              editable: editShipment,
            }}
          />
          <Panel>
            <COHeadline variant={'h2'} title={tr(`ExpeditionDetail.route`, `Route`)} icon={<SvgRoute />}></COHeadline>
            <div className={'mb-4 mt-4'}>
              <RowButton
                title={showMap ? tr('ExpeditionDetail.hideMap', 'Hide map') : tr('ExpeditionDetail.routeOnMap', 'Route on map')}
                actionIcon={<SvgChevron className={clsx('size-4 shrink-0 transform transition-transform duration-200', { 'rotate-180': showMap })} />}
                onClick={() => setShowMap(!showMap)}
              />
              {showMap && (
                <div className={'mt-4 rounded-2xl bg-white p-4'}>
                  <HereMaps customerId={expedition.customerId} applicationId={expedition.applicationId} />
                </div>
              )}
            </div>
            {expedition.locations.map((location, locationIndex) => {
              const positionSuffix =
                locationIndex === 0
                  ? 'First'
                  : locationIndex === expedition.locations.length - 1
                    ? 'Last'
                    : `Mid${locationIndex}`;

              const testIdPrefix = `location${positionSuffix}`;

              return (
                <React.Fragment key={locationIndex}>
                  <NavigationBreadCrumb
                    currentPosition={locationIndex}
                    locationCount={expedition.locations.length}
                    idPrefix={'createExp'}
                    locations={expedition.locations.map(location => ({
                      id: location.id,
                      name: location.name,
                    }))}
                  />
                  {location.requirementCodes && location.requirementCodes.length > 0 && (
                    <Ringil3BadgeRow>
                      <RequirementsTag
                        valueCodes={location.requirementCodes}
                        companyId={expedition.customerId}
                        onChange={() => {
                        }}
                        readOnly={true}
                      />
                    </Ringil3BadgeRow>
                  )}
                  <ExpeditionLocation
                    isLastLocation={locationIndex === expedition.locations.length - 1}
                    isFirstLocation={locationIndex === 0}
                    location={location}
                    applicationId={expedition.applicationId}
                    expeditionId={expeditionId}
                    testIdPrefix={testIdPrefix}
                    allUnloadingLocations={expedition.locations
                      .filter((_, index) => index > locationIndex)
                      .map(t => ({
                        id: t.id,
                        name: t.name,
                      }))}
                    onReload={() => load()}
                    canEdit={editShipmentPermission}
                    expeditionStatus={expedition.expeditionStatus}
                    linkedToSN={expedition.supplierNotificationLink != null}
                    volumeOfOrderEnabled={expedition.volumeOfOrderEnabled}
                    secured={{ id: 'location', instance: String(locationIndex), label: 'ExpeditionLocation', description: "One stop (loading or unloading place) on the shipment's route", editable: editShipment }}
                  />
                </React.Fragment>
              );
            })}
          </Panel>
        </>
      )}
      {selectedPanel === 'customs' && (
        <CustomsDetail
          expeditionId={expedition.id}
          applicationId={expedition.applicationId}
          readonly={isImportedState}
          secured={{ id: 'tab-customs', label: 'CustomsDetail', description: 'Customs details of the shipment', visible: customsPermission }}
        />
      )}
      {selectedPanel === 'internalInfoAndHistory' && (
        <>
          {extraCostsCollapsed ? (
            <SectionPanel
              icon={<SvgExtracosts />}
              heading={tr(`ExtraCosts.heading`, `Total cost: {{amountWithCurrency}}`, {
                amountWithCurrency: extraCostsTotal(expedition.sumPrice?.finalPrice, expedition.extraCosts),
              })}
              deferred={{
                description: tr('ExpeditionDetail.totalCostsHint', 'Transport costs including extra costs.'),
                revealLabel: tr('ExpeditionDetail.showTotalCosts', 'Show total costs'),
                dataTestId: 'expeditionDetail-reveal-totalCosts',
              }}>
              {extraCostsSection(true)}
            </SectionPanel>
          ) : (
            extraCostsSection(false)
          )}
          {showDistributionHistory &&
            (tenderHistoryCollapsed ? (
              <SectionPanel
                icon={<SvgLkw />}
                heading={tr('ExpeditionDetail.distributionHistory', 'Selection procedure history')}
                deferred={{
                  description: tr('ExpeditionDetail.distributionHistoryHint', 'Carriers and their quotes within the selection procedure.'),
                  revealLabel: tr('ExpeditionDetail.showDistributionHistory', 'Show selection procedure history'),
                  dataTestId: 'expeditionDetail-reveal-distributionHistory',
                }}>
                {distributionHistorySection()}
              </SectionPanel>
            ) : (
              distributionHistorySection()
            ))}
          <SecuredArea id="tab-audit-log" label="Audit log section" description="History of all changes made to the shipment" visible={showAudit}>
            {auditLogCollapsed ? (
              <SectionPanel
                icon={<SvgAuditlog />}
                heading={tr(`AuditLog.heading`, `Audit log`)}
                deferred={{
                  description: tr('ExpeditionDetail.auditLogHint', 'All actions performed by you or your colleagues on this shipment.'),
                  revealLabel: tr('ExpeditionDetail.showAuditLog', 'Show action history'),
                  dataTestId: 'expeditionDetail-reveal-auditLog',
                }}>
                <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.EXPEDITION} entityId={expedition.id} hideHeader />
              </SectionPanel>
            ) : (
              <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.EXPEDITION} entityId={expedition.id} />
            )}
          </SecuredArea>
          <COHeadline variant={'h3'} title={tr('ExpeditionDetail.interniInfo', 'Interní info')} />
          <SecuredArea id="tab-internal-info" label="Internal info section" description="The company's internal details on the shipment: responsible person, folder, invoicing, code lists, internal note" visible={showInternalInfo}>
            <div className={'flex flex-col gap-4'}>
              <Ringil3BadgeRow>
                <ResponsiblePersonTag
                  person={
                    isNotNullOrUndefined(expedition.internalInfo?.responsiblePerson)
                      ? ({
                          name: expedition.internalInfo?.responsiblePerson.userFullName,
                          id: expedition.internalInfo?.responsiblePerson.userId,
                        } as LovDTO)
                      : null
                  }
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, { responsiblePerson: { responsiblePersonId: v?.id } }, () => load());
                  }}
                  secured={{ id: 'ii-responsible-person', label: 'ResponsiblePersonTag', description: "Shipment internal info: the user's company's person responsible for the shipment", editable: editInternalInfoResponsiblePerson }}
                />

                <NotifyUsersTag
                  notifiedTargets={expedition.internalInfo?.notifiedPerson?.map(t => ({
                    id: t.userId,
                    notifyTargetType: NotifyTargetTypeEnum.USER,
                  }))}
                  onChange={notifyTargets => setNotifyTargets(expeditionId, { targets: notifyTargets }, () => load())}
                  secured={{ id: 'ii-notify-users', label: 'NotifyUsersTag', description: 'Shipment internal info: users notified about the shipment', editable: editInternalInfo }}
                />

                <InternalInfoFoldersTag
                  folders={(expedition.internalInfo?.folders ?? []).map(f => ({ id: f.folderId, name: f.folderName, active: true }) as LovDTO)}
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, { folders: { folderIds: v.map(f => f.id) } }, () => load());
                  }}
                  entityType={EntityTypeEnum.EXPEDITION}
                  secured={{ id: 'ii-folders', label: 'InternalInfoFoldersTag', description: 'Shipment internal info: folders the shipment is filed in', editable: editInternalInfo }}
                />
              </Ringil3BadgeRow>
              <Ringil3BadgeRow>
                <InternalInfoInvoiceState
                  value={expedition.internalInfo?.invoiceState}
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, { invoiceState: { invoiceState: v } }, () => load());
                  }}
                  secured={{ id: 'ii-invoice-state', label: 'InternalInfoInvoiceState', description: 'Shipment internal info: invoicing state', editable: editInternalInfo }}
                />

                <InternalInfoDisponentTag
                  disponentPerson={
                    isNotNullOrUndefined(expedition.internalInfo?.disponentPerson)
                      ? {
                          name: expedition.internalInfo?.disponentPerson.userFullName,
                          id: expedition.internalInfo?.disponentPerson.userId,
                          active: true,
                        }
                      : null
                  }
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, { disponentPerson: { disponentPersonId: v?.id } }, () => load());
                  }}
                  secured={{ id: 'ii-disponent', label: 'InternalInfoDisponentTag', description: 'Shipment internal info: dispatcher handling the shipment', editable: editInternalInfo }}
                />

                <InternalInfoPairingSymbol
                  value={expedition.internalInfo?.pairingSymbol}
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, { pairingSymbol: { pairingSymbol: v } }, () => load());
                  }}
                  secured={{ id: 'ii-pairing-symbol', label: 'InternalInfoPairingSymbol', description: 'Shipment internal info: pairing symbol for matching invoices', editable: editInternalInfo }}
                />

                <InternalNoteTag
                  value={expedition.internalInfo?.internalNote}
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, { internalNote: { internalNote: v } }, () => load());
                  }}
                  secured={{ id: 'ii-internal-note', label: 'InternalNoteTag', description: 'Shipment internal info: internal note', editable: editInternalInfo }}
                />

                <InternalInfoRebillingTag
                  value={expedition.internalInfo?.rebilling}
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, { rebilling: { rebilling: mapRebilling(v) } }, () => load());
                  }}
                  secured={{ id: 'ii-rebilling', label: 'InternalInfoRebillingTag', description: 'Shipment internal info: rebilling of the transport costs', editable: editInternalInfo }}
                />
                <InternalInfoCustomDeclarantTag
                  customDeclarationCompanies={expedition.internalInfo?.customsDeclarationCompanies || []}
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, { customsDeclarationCompanies: { companies: v.map(value => value.id) } }, () => load());
                  }}
                  secured={{ id: 'ii-customs-declarant', label: 'InternalInfoCustomDeclarantTag', description: 'Shipment internal info: customs declarant', editable: editInternalInfo }}
                />
                <InternalInfoCatalogTag
                  type={CodeTypeEnum.ACCOUNT}
                  value={expedition.internalInfo?.internalInfoAccount?.id}
                  onChange={v => {
                    editInternalInfoExpedition(
                      expeditionId,
                      {
                        companyCodeLists: {
                          internalInfoAccountCodeListId: v,
                          internalInfoCostCenterCodeListId: expedition.internalInfo?.internalInfoCostCenter?.id,
                          internalInfoCostUnitCodeListId: expedition.internalInfo?.internalInfoCostUnit?.id,
                          internalInfoProjectCodeListId: expedition.internalInfo?.internalInfoProject?.id,
                        },
                      },
                      () => load(),
                    );
                  }}
                  secured={{ id: 'ii-catalog-account', label: 'InternalInfoCatalogTag (ACCOUNT)', description: 'Shipment internal info: account from the company code list', editable: editInternalInfo }}
                />
                <InternalInfoCatalogTag
                  type={CodeTypeEnum.COST_CENTER}
                  value={expedition.internalInfo?.internalInfoCostCenter?.id}
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, {
                      companyCodeLists: {
                        internalInfoAccountCodeListId: expedition.internalInfo?.internalInfoAccount?.id,
                        internalInfoCostCenterCodeListId: v,
                        internalInfoCostUnitCodeListId: expedition.internalInfo?.internalInfoCostUnit?.id,
                        internalInfoProjectCodeListId: expedition.internalInfo?.internalInfoProject?.id,
                      },
                    }, () => load());
                  }}
                  secured={{ id: 'ii-catalog-cost-center', label: 'InternalInfoCatalogTag (COST_CENTER)', description: 'Shipment internal info: cost center from the company code list', editable: editInternalInfo }}
                />
                <InternalInfoCatalogTag
                  type={CodeTypeEnum.COST_UNIT}
                  value={expedition.internalInfo?.internalInfoCostUnit?.id}
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, {
                      companyCodeLists: {
                        internalInfoAccountCodeListId: expedition.internalInfo?.internalInfoAccount?.id,
                        internalInfoCostCenterCodeListId: expedition.internalInfo?.internalInfoCostCenter?.id,
                        internalInfoCostUnitCodeListId: v,
                        internalInfoProjectCodeListId: expedition.internalInfo?.internalInfoProject?.id,
                      },
                    }, () => load());
                  }}
                  secured={{ id: 'ii-catalog-cost-unit', label: 'InternalInfoCatalogTag (COST_UNIT)', description: 'Shipment internal info: cost unit from the company code list', editable: editInternalInfo }}
                />
                <InternalInfoCatalogTag
                  type={CodeTypeEnum.PROJECT}
                  value={expedition.internalInfo?.internalInfoProject?.id}
                  onChange={v => {
                    editInternalInfoExpedition(expeditionId, {
                      companyCodeLists: {
                        internalInfoAccountCodeListId: expedition.internalInfo?.internalInfoAccount?.id,
                        internalInfoCostCenterCodeListId: expedition.internalInfo?.internalInfoCostCenter?.id,
                        internalInfoCostUnitCodeListId: expedition.internalInfo?.internalInfoCostUnit?.id,
                        internalInfoProjectCodeListId: v,
                      },
                    }, () => load());
                  }}
                  secured={{ id: 'ii-catalog-project', label: 'InternalInfoCatalogTag (PROJECT)', description: 'Shipment internal info: project from the company code list', editable: editInternalInfo }}
                />
              </Ringil3BadgeRow>
            </div>
          </SecuredArea>
        </>
      )}
      {selectedPanel === 'cmr' && (
        <CMRTableTab
          expeditionId={expeditionId}
          readOnly={!editCmrPermission}
          secured={{ id: 'tab-cmr', label: 'CMRTableTab', description: "The shipment's CMR (consignment note) records", visible: showCmr, editable: editCmr }}
        />
      )}

      {selectedPanel === 'attachments' && (
        <AttachmentComponent
          internals={attachmentInternals}
          entityId={expeditionId}
          entityType={EntityTypeEnum.EXPEDITION}
          disabled={!editAttachmentsPermission}
          secured={{ id: 'tab-attachments', label: 'AttachmentComponent', description: "The shipment's documents and attachments", visible: showAttachments, editable: editAttachments }}
        />
      )}

      <CustomerChatComponent
        companyId={expedition.customerId}
        entityId={expeditionId}
        entityType={EntityTypeEnum.EXPEDITION}
        visible={isMessagesOpen}
        selectedCarrierId={selectedCarrierId}
        onHide={() => {
          setSelectedCarrierId(undefined);
          setIsMessagesOpen(false);
        }}
        readOnly={!editChatPermission}
        secured={{ id: 'chat-dialog', label: 'CustomerChatComponent', description: 'Messages with the carriers about the shipment', visible: showChat, editable: editChat }}
      />

      <InternalCommentsSidebar
        visible={isCommentsOpen}
        onHide={() => setIsCommentsOpen(false)}
        entityId={expeditionId}
        applicationId={expedition.applicationId}
        editFlag={editInternalComment}
      />

      <UpdateExpeditionNameSidebar expeditionId={expeditionId} applicationId={expedition.applicationId} currentName={expedition.name} onContinue={name => load()} />

      <RequestCarrierSidebar
        onContinue={() => {
          onSuggestionDistributed.current?.();
          onSuggestionDistributed.current = undefined;
          setSuggestedCarrierIds(undefined);
          load();
        }}
        preselectedCarrierIds={suggestedCarrierIds}
        handlingUnitCount={expedition.handlingUnitCount}
        ladenFactorEligible={isLadenFactorEligible(expedition, hasFeature([FeaturesEnum.LADEN_FACTOR]))}
        aiCarrierAutoSelectionDto={getAiCarrierAutoSelectionDto(expedition)}
        expedition={expedition}
      />
    </>
  );
};

export default ExpeditionDetail;
